import { useMemo } from "react";
import {
    BookOpen, MessageCircle, Headphones, Mic, Briefcase, Flame,
    Trophy, Repeat, Sparkles, Award, Languages, Lock,
} from "lucide-react";

const ICONS = {
    "book-open": BookOpen,
    "message-circle": MessageCircle,
    "headphones": Headphones,
    "mic": Mic,
    "briefcase": Briefcase,
    "flame": Flame,
    "trophy": Trophy,
    "repeat": Repeat,
    "sparkles": Sparkles,
    "award": Award,
    "languages": Languages,
};

const TIER_STYLE = {
    bronze: { bg: "bg-amber-100", ring: "ring-amber-400", text: "text-amber-800", label: "Bronze" },
    argent: { bg: "bg-slate-100", ring: "ring-slate-400", text: "text-slate-700", label: "Argent" },
    or: { bg: "bg-terracotta/15", ring: "ring-terracotta", text: "text-terracotta", label: "Or" },
};

export default function BadgeCard({ badge }) {
    const Icon = ICONS[badge.icon] || Trophy;
    const tier = TIER_STYLE[badge.tier] || TIER_STYLE.bronze;
    const earned = !!badge.earned;

    const dateLabel = useMemo(() => {
        if (!badge.earned_at) return null;
        try {
            const d = new Date(badge.earned_at);
            return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
        } catch {
            return null;
        }
    }, [badge.earned_at]);

    return (
        <div
            data-testid={`badge-${badge.code}`}
            data-earned={earned ? "true" : "false"}
            className={`relative rounded-2xl p-4 border transition-all ${earned
                ? `${tier.bg} border-transparent ring-1 ${tier.ring}`
                : "bg-paperAlt border-lexiBorder opacity-70"}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${earned
                        ? "bg-white"
                        : "bg-paper"}`}
                >
                    {earned ? (
                        <Icon className={`w-5 h-5 ${tier.text}`} strokeWidth={1.75} />
                    ) : (
                        <Lock className="w-4 h-4 text-inkSoft" strokeWidth={1.75} />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                        <p className="font-serif text-base leading-tight">{badge.title_fr}</p>
                        <span
                            className={`text-[9px] tracking-[0.22em] uppercase font-semibold shrink-0 ${earned ? tier.text : "text-inkSoft"}`}
                        >
                            {tier.label}
                        </span>
                    </div>
                    <p className="mt-1 text-[12px] text-inkSoft leading-snug">
                        {earned ? badge.description_fr : badge.criterion_fr}
                    </p>
                    {earned && dateLabel && (
                        <p
                            className="mt-2 text-[10px] tracking-[0.22em] uppercase text-inkSoft"
                            data-testid={`badge-${badge.code}-date`}
                        >
                            Débloqué le {dateLabel}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
